import { useState, useMemo } from 'react'
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, format, isSameMonth, isSameDay, isToday,
} from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useCustodySchedules, useCustodyCalendar, useCustodyExceptions } from '../hooks/useCustody'
import { ExceptionModal } from './ExceptionModal'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'
import { Spinner } from '../../../components/ui/Spinner'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Returns array of week rows for the grid
function buildCalendarDays(month) {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 })
  const end   = endOfWeek(endOfMonth(month), { weekStartsOn: 0 })
  const days  = []
  let cur = start
  while (cur <= end) {
    days.push(new Date(cur))
    cur = addDays(cur, 1)
  }
  return days
}

// Color-filled cell for a day — shows schedule colors as horizontal bands
const DayCell = ({ date, inMonth, schedules, calendarData, exceptions, isSelected, onClick }) => {
  const dateStr = format(date, 'yyyy-MM-dd')
  const todayFlag = isToday(date)

  // Find which schedules have exceptions on this day
  const dayExceptions = exceptions.filter((e) => e.exception_date === dateStr)
  const hasException  = dayExceptions.length > 0

  // Determine custody for each active schedule
  const assignments = schedules
    .filter((s) => s.is_active)
    .map((s) => {
      const householdId = calendarData?.[dateStr]?.[s.id] ?? null
      return { schedule: s, householdId }
    })
    .filter((a) => a.householdId !== null)

  const bandCount = assignments.length

  return (
    <button
      onClick={() => onClick(date, assignments)}
      className={cn(
        'relative flex flex-col items-center justify-start rounded-lg overflow-hidden transition-all',
        'border text-xs font-medium aspect-square',
        inMonth ? 'bg-surface' : 'bg-background opacity-50',
        isSelected ? 'ring-2 ring-primary ring-offset-1' : '',
        todayFlag ? 'border-primary' : 'border-border',
      )}
    >
      {/* Color bands — one per active schedule */}
      {bandCount > 0 && (
        <div className="absolute inset-0 flex flex-col">
          {assignments.map(({ schedule, householdId }) => (
            <div
              key={schedule.id}
              className="flex-1"
              style={{ backgroundColor: schedule.color + '55' }}
            />
          ))}
        </div>
      )}

      {/* Date number */}
      <span className={cn(
        'relative z-10 mt-1 w-5 h-5 flex items-center justify-center rounded-full text-xs',
        todayFlag ? 'bg-primary text-white font-bold' : 'text-text-primary',
      )}>
        {format(date, 'd')}
      </span>

      {/* Exception star */}
      {hasException && (
        <span className="relative z-10 text-amber-500 text-xs leading-none mt-auto mb-0.5">★</span>
      )}
    </button>
  )
}

// Panel shown when tapping a day
const DayPanel = ({ date, assignments, exceptions, schedules, onAddException, onClose }) => {
  const dateStr = format(date, 'yyyy-MM-dd')
  const dayExceptions = exceptions.filter((e) => e.exception_date === dateStr)

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] overflow-y-auto shadow-card p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-text-primary">
            {format(date, 'EEEE, MMMM d, yyyy')}
          </h3>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {assignments.length === 0 ? (
          <p className="text-sm text-text-muted">No active custody schedules for this day.</p>
        ) : (
          <div className="space-y-2">
            {assignments.map(({ schedule, householdId, householdName }) => (
              <div key={schedule.id} className="flex items-center gap-3 bg-background rounded-xl px-4 py-3">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: schedule.color }} />
                <div>
                  <p className="text-sm font-semibold text-text-primary">{schedule.schedule_name}</p>
                  <p className="text-xs text-text-muted">{schedule.child?.display_name} · {householdName}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {dayExceptions.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Exceptions</p>
            <div className="space-y-1">
              {dayExceptions.map((ex) => (
                <div key={ex.id} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2">
                  <p className="text-xs font-semibold text-amber-700">
                    {ex.reason?.replace('_', ' ') ?? 'Exception'}
                  </p>
                  {ex.notes && <p className="text-xs text-amber-600 mt-0.5">{ex.notes}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {onAddException && (
          <button
            type="button"
            onClick={() => onAddException(date, assignments[0])}
            className="w-full border border-dashed border-border rounded-xl py-2.5 text-sm font-medium text-text-secondary hover:border-primary hover:text-primary transition-colors"
          >
            + Add Exception
          </button>
        )}
      </div>
    </div>
    </Portal>
  )
}

export const CustodyCalendar = ({ schedules, filterChildId }) => {
  const { household, isAdmin } = useAuth()
  const { activeLink }          = useLinkedHouseholds()

  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'
  const ourName             = household?.name ?? 'Your House'

  const [month,          setMonth]          = useState(new Date())
  const [selectedDay,    setSelectedDay]    = useState(null)
  const [selectedAssign, setSelectedAssign] = useState([])
  const [showException,  setShowException]  = useState(false)
  const [exceptionSchedule, setExceptionSchedule] = useState(null)
  const [exceptionOrigHH,   setExceptionOrigHH]   = useState(null)

  const visibleSchedules = useMemo(() => {
    if (!filterChildId) return schedules
    return schedules.filter((s) => s.child_id === filterChildId)
  }, [schedules, filterChildId])

  const monthStart = startOfMonth(month)
  const monthEnd   = endOfMonth(month)

  const { data: calendarData, isLoading } = useCustodyCalendar(
    visibleSchedules,
    startOfWeek(monthStart, { weekStartsOn: 0 }),
    endOfWeek(monthEnd, { weekStartsOn: 0 })
  )

  // Load all exceptions for visible schedules
  const allExceptions = []
  // (Loaded per-schedule in the schedule detail — here we query in bulk)

  const days = buildCalendarDays(month)

  const enrichedAssignments = (assignments) => assignments.map((a) => ({
    ...a,
    householdName: a.householdId === household?.id ? ourName : linkedHouseholdName,
  }))

  const handleDayClick = (date, assignments) => {
    setSelectedDay(date)
    setSelectedAssign(enrichedAssignments(assignments))
  }

  const handleAddException = (date, firstAssignment) => {
    if (!firstAssignment) return
    setExceptionSchedule(firstAssignment.schedule)
    setExceptionOrigHH(firstAssignment.householdId)
    setShowException(true)
  }

  return (
    <div>
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setMonth((m) => subMonths(m, 1))}
          className="p-2 text-text-muted hover:text-primary transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2 className="text-base font-bold text-text-primary">{format(month, 'MMMM yyyy')}</h2>
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="p-2 text-text-muted hover:text-primary transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center text-xs font-semibold text-text-muted py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {days.map((date) => (
            <DayCell
              key={date.toISOString()}
              date={date}
              inMonth={isSameMonth(date, month)}
              schedules={visibleSchedules}
              calendarData={calendarData ?? {}}
              exceptions={allExceptions}
              isSelected={selectedDay && isSameDay(date, selectedDay)}
              onClick={handleDayClick}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      {visibleSchedules.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {visibleSchedules.filter((s) => s.is_active).map((s) => (
            <div key={s.id} className="flex items-center gap-1.5 text-xs text-text-secondary">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
              <span>{s.schedule_name}</span>
            </div>
          ))}
        </div>
      )}

      {selectedDay && !showException && (
        <DayPanel
          date={selectedDay}
          assignments={selectedAssign}
          exceptions={allExceptions}
          schedules={visibleSchedules}
          onAddException={isAdmin ? handleAddException : null}
          onClose={() => setSelectedDay(null)}
        />
      )}

      {showException && exceptionSchedule && (
        <ExceptionModal
          schedule={exceptionSchedule}
          prefillDate={selectedDay}
          originalHouseholdId={exceptionOrigHH}
          onClose={() => {
            setShowException(false)
            setSelectedDay(null)
          }}
        />
      )}
    </div>
  )
}
